import { ThreadStatusCountsMap } from "@ui/agent/components/agent/api/chat-api";
import { createContext, useCallback, useState } from "react";

import { ThreadEntity } from "@/lib/types/Thread";

type ChatsContextType = {
  allChats: ThreadEntity[];
  setAllChats: React.Dispatch<React.SetStateAction<ThreadEntity[]>>;
  appendChats: (newChats: ThreadEntity[]) => void;
  hasMore: boolean;
  setHasMore: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  threadsRefreshToken: number;
  refreshThreads: () => void;
  statusCounts: ThreadStatusCountsMap | undefined;
  setStatusCounts: React.Dispatch<
    React.SetStateAction<ThreadStatusCountsMap | undefined>
  >;
};

export const ChatsContext = createContext<ChatsContextType | undefined>(
  undefined,
);

export const ChatsProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const [allChats, setAllChats] = useState<ThreadEntity[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [statusCounts, setStatusCounts] = useState<ThreadStatusCountsMap>();
  const [threadsRefreshToken, setThreadsRefreshToken] = useState(0);

  const refreshThreads = useCallback(() => {
    setThreadsRefreshToken((token) => token + 1);
  }, []);

  const appendChats = useCallback((newChats: ThreadEntity[]) => {
    setAllChats((prev) => {
      const existingIds = new Set(prev.map((c) => c.chat_id));
      const unique = newChats.filter((c) => !existingIds.has(c.chat_id));
      return [...prev, ...unique];
    });
  }, []);

  return (
    <ChatsContext.Provider
      value={{
        allChats,
        setAllChats,
        appendChats,
        hasMore,
        setHasMore,
        isLoading,
        setIsLoading,
        threadsRefreshToken,
        refreshThreads,
        statusCounts,
        setStatusCounts,
      }}
    >
      {children}
    </ChatsContext.Provider>
  );
};
